import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
	selector: 'app-create-note-modal',
	templateUrl: './create-note-modal.component.html',
	styleUrls: ['./create-note-modal.component.scss'],
})
export class CreateNoteModalComponent implements OnInit, OnDestroy {
	formGroup: FormGroup;
	protected _onDestroy = new Subject<void>();
	isSaving = false;

	constructor(
		private readonly formBuilder: FormBuilder,
		private readonly apiService: LegacyApiService,
		private readonly snackBar: MatSnackBar,
		public dialogRef: MatDialogRef<CreateNoteModalComponent>,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: any
	) {
		// remark is only required in create mode (when noteId is not present)
		this.formGroup = this.formBuilder.group({
			remark: [null, this.data?.noteId ? [] : [Validators.required]],
		});
	}

	ngOnInit() {
		if (this.data?.remark) {
			this.formGroup.patchValue({
				remark: this.data.remark,
			});
		}
	}

	dismiss() {
		this.dialogRef.close(null);
	}

	saveRemark() {
		this.isSaving = true;
		const noteData = {
			subject: this.data.subject,
			description: this.data.description,
			note: this.formGroup.controls.remark.value,
			communication: 'INT',
		};

		if (this.data?.noteId) {
			// Update existing note
			this.updateNote(this.data.noteId, noteData);
		} else {
			// Create new note
			this.createNote(noteData);
		}
	}

	private createNote(noteData: any) {
		this.apiService
			.createNoteLocalDB(
				noteData,
				this.data.application.Deal_Name,
				this.data.application.id,
				this.data.currentUser.full_name,
				this.data.currentUser.zoho_owner_id,
				false,
				'',
			)
			.subscribe({
				next: (res) => {
					if (res.message === 'Success') {
						const app_note_id = res['data'][0]['app_note_id'] ?? '';

						// Push to ZOHO
						this.apiService
							.addLoanInterviewNotesZOHO(
								noteData,
								this.data.application.id,
								this.data.currentUser.zoho_owner_id
							)
							.subscribe({
								next: (zohoRes) => {
									if (zohoRes['message']['statusCode'] === 201) {
										const zoho_note_id = zohoRes['app_id'];
										this.apiService
											.updateNoteIDLocal(app_note_id, zoho_note_id)
											.subscribe();

										this.snackBar.open('Note saved successfully!', 'Close', {
											duration: 3000,
											panelClass: ['success-snackbar'],
										});
										this.dialogRef.close({ success: true });
									} else {
										this.handleError('Failed to save note to ZOHO');
									}
								},
								error: () => this.handleError('Failed to save note to ZOHO'),
							});
					} else {
						this.handleError('Failed to save note!');
					}
				},
				error: () => this.handleError('Failed to save note!'),
			});
	}

	private updateNote(noteId: string, noteData: any) {
		this.apiService
			.updateNoteLocalDB(
				noteId,
				noteData,
				this.data.application.Deal_Name,
				this.data.application.id,
				this.data.currentUser.full_name,
				this.data.currentUser.zoho_owner_id
			)
			.subscribe({
				next: (res) => {
					if (res.message === 'Success') {
						this.snackBar.open('Note updated successfully!', 'Close', {
							duration: 3000,
							panelClass: ['success-snackbar'],
						});
						this.dialogRef.close({ success: true });
					} else {
						this.handleError('Failed to update note!');
					}
				},
				error: () => this.handleError('Failed to update note!'),
			});
	}

	private handleError(message: string) {
		this.isSaving = false;
		this.snackBar.open(message, 'Close', {
			duration: 5000,
			panelClass: ['error-snackbar'],
		});
	}

	ngOnDestroy() {
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}